import React, { useRef, useContext, useEffect, useCallback } from 'react'
import { getSrc } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { StoreState, StoreDispatch } from '../../Store'
import ImagesLoaded from 'imagesloaded'

// Components
import Button from '../Button'
import Container from '../Container'
import Grid from '../Grid'
import GridItem from '../GridItem'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'
import RichText from '../RichText'
import AnimateScaleIn from '../animation/AnimateScaleIn'
import AnimateSlideText from '../animation/AnimateSlideText'
import AnimateSlideIn from '../animation/AnimateSlideIn'

// Vars
import { Label } from '../TextStyles'
import { colors } from '../../styles/vars/colors.style'

// Styles
import {
  BannerWrapper,
  BannerInner,
  BannerImageWrap,
  BannerImage,
  BannerText,
  SmallImage,
} from './index.style'

const FullWidthBanner = ({
  id,
  label,
  labelColor,
  title,
  text,
  image,
  button,
  hasHeadingOne,
  smallImage,
  linkId,
}) => {
  const innerRef = useRef(null)

  const imageSource = getSrc(image.gatsbyImageData)

  const store = useContext(StoreState)
  const dispatch = useContext(StoreDispatch)
  const { waiting } = store

  const initialDelay = hasHeadingOne ? 2 : 0

  const loaded = useCallback(() => {
    setTimeout(() => {
      dispatch({ type: 'END_INITIAL_WAIT' })
      dispatch({ type: 'HIDE_TRANSITION_MASK' })
    }, 5000)
  }, [dispatch])

  useEffect(() => {
    if (!hasHeadingOne || !innerRef.current) return
    dispatch({ type: 'BEGIN_INITIAL_WAIT' })
    const imageLoadTrack = new ImagesLoaded(innerRef.current)

    imageLoadTrack.on('always', loaded)

    return () => {
      imageLoadTrack.off('always', loaded)
    }
  }, [dispatch, loaded, innerRef, hasHeadingOne])

  return (
    <ScrollSection>
      <BannerWrapper id={linkId && linkId}>
        <BannerInner ref={innerRef}>
          <BannerImageWrap>
            <AnimateScaleIn id={`${id}-image`} syncWithScroll={!hasHeadingOne}>
              <BannerImage image={imageSource} inView={!waiting} />
            </AnimateScaleIn>
          </BannerImageWrap>
          <BannerText>
            <Spacer size={[100, 250]} />
            <Container>
              <Grid>
                <GridItem tabletP={8} tabletPStart={3}>
                  {label ? (
                    <>
                      <AnimateSlideText
                        id={`${id}-label`}
                        delay={initialDelay}
                        useInView={!hasHeadingOne}
                        isInView={!waiting}
                      >
                        <Label bgColor={labelColor}>{label}</Label>
                      </AnimateSlideText>
                      <Spacer size={[30, 68]} />
                    </>
                  ) : (
                    ``
                  )}
                  <RichText
                    id={`${id}-heading`}
                    content={title}
                    delay={initialDelay + 0.1}
                    delayEm={true}
                    upgradeHeading
                    forceHeading
                    hasHeadingOne={hasHeadingOne}
                    useInView={!hasHeadingOne}
                    isInView={!waiting}
                  />
                  <Spacer size={30} />
                  {smallImage && (
                    <>
                      <SmallImage>
                        <AnimateSlideIn
                          delay={initialDelay + 0.2}
                          useInView={!hasHeadingOne}
                          isInView={!waiting}
                        >
                          <img
                            src={
                              smallImage?.gatsbyImageData.images.fallback.src
                            }
                            alt={''}
                            height={150}
                          />
                        </AnimateSlideIn>
                      </SmallImage>
                      <Spacer size={30} />
                    </>
                  )}
                  {text && (
                    <>
                      <RichText
                        id={`${id}-text`}
                        content={text}
                        delay={initialDelay + 0.2}
                        useInView={!hasHeadingOne}
                        isInView={!waiting}
                      />
                      <Spacer size={30} />
                    </>
                  )}
                  {button && (
                    <>
                      <Spacer size={[30, 52]} />
                      <AnimateSlideText
                        id={`${id}-cta`}
                        delay={initialDelay + 0.3}
                        useInView={!hasHeadingOne}
                        isInView={!waiting}
                      >
                        <Button
                          href={button.externalLink}
                          variant="underline"
                          color={colors.light}
                        >
                          {button.label}
                        </Button>
                      </AnimateSlideText>
                    </>
                  )}
                </GridItem>
              </Grid>
            </Container>
            <Spacer size={[100, 250]} />
          </BannerText>
        </BannerInner>
      </BannerWrapper>
    </ScrollSection>
  )
}

FullWidthBanner.propTypes = {
  id: PropTypes.string.isRequired,
  button: PropTypes.object,
  label: PropTypes.string,
  heading: PropTypes.object,
  text: PropTypes.object,
}

export default FullWidthBanner
